import React, { MouseEvent } from 'react';
import styled, { css } from 'styled-components/macro';
import { colourNeutral01, colourNeutral03, colourNeutral11, colourPrimarySeaGreen } from '../styling/colours';
import { fontFamily, fontSize02 } from '../styling/fonts';
import { spacing02, spacing03, spacing05 } from '../styling/spacing';
import { LoadingIndicator } from './LoadingIndicator';

export type ButtonProps = {
  onClick?: (event: MouseEvent) => void;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  rounded?: boolean;
};

export const getButtonStyles = (rounded?: boolean, disabled?: boolean) => css`
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  outline: none;
  border: solid 1px transparent;
  border-radius: ${rounded ? '100px' : 0};
  padding: 0 ${spacing05};
  font-family: ${fontFamily};
  font-size: ${fontSize02};
  transition: all 0.25s ease;
  position: relative;
  height: 40px;

  cursor: ${disabled ? 'not-allowed' : 'pointer'};
  opacity: ${disabled ? 0.5 : null};

  &:hover,
  &:focus {
    opacity: ${disabled ? 0.5 : 0.7};
  }
`;

export const BaseButton = styled.button.attrs(props => ({ type: props.type || 'button' }))<ButtonProps>`
  ${({ rounded, disabled }) => getButtonStyles(rounded, disabled)};
`;

export const primaryButtonStyles = css`
  background-color: ${colourPrimarySeaGreen};
  color: ${colourNeutral11};
`;

export const PrimaryButton = styled(BaseButton)<ButtonProps>`
  ${primaryButtonStyles}
`;

export const secondaryButtonStyles = css`
  color: ${colourNeutral03};
  border-color: rgba(0, 0, 0, 0.15);
  background-color: rgba(255, 255, 255, 0.8);
`;

export const SecondaryButton = styled(BaseButton)<ButtonProps>`
  ${secondaryButtonStyles}
`;

export const LinkButton = styled(BaseButton)<ButtonProps>`
  background: none;
  color: ${colourPrimarySeaGreen};
`;

type ProgressButtonProps = ButtonProps & {
  inProgress: boolean;
};

export const PrimaryProgressButton = ({ children, disabled, inProgress, ...props }: ProgressButtonProps) => {
  return (
    <PrimaryButton {...props} disabled={disabled || inProgress}>
      <ProgressButtonContentContainer $visible={!inProgress}>{children}</ProgressButtonContentContainer>
      <ProgressButtonLoadingIndicatorContainer $visible={inProgress}>
        <LoadingIndicator size={5} colour={colourNeutral11} />
      </ProgressButtonLoadingIndicatorContainer>
    </PrimaryButton>
  );
};

export const SecondaryProgressButton = ({ children, disabled, inProgress, ...props }: ProgressButtonProps) => {
  return (
    <SecondaryButton {...props} disabled={disabled || inProgress}>
      <ProgressButtonContentContainer $visible={!inProgress}>{children}</ProgressButtonContentContainer>
      <ProgressButtonLoadingIndicatorContainer $visible={inProgress}>
        <LoadingIndicator size={5} colour={colourNeutral01} />
      </ProgressButtonLoadingIndicatorContainer>
    </SecondaryButton>
  );
};

const ProgressButtonContentContainer = styled.div<{ $visible: boolean }>`
  opacity: ${props => (props.$visible ? 1 : 0)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ProgressButtonLoadingIndicatorContainer = styled.div<{ $visible: boolean }>`
  opacity: ${props => (props.$visible ? 1 : 0)};
  position: absolute;
  top: 55%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export const ButtonIcon = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: -${spacing02};
  margin-right: -${spacing02};

  svg {
    height: 22px;
    width: 22px;
  }
`;

export const ButtonIconLeft = styled(ButtonIcon)`
  margin-right: ${spacing02};
`;

export type ButtonRowAlignment = 'left' | 'right' | 'apart';

export const ButtonRow = styled.div<{ $align: ButtonRowAlignment }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;

  justify-content: ${({ $align }) => {
    switch ($align) {
      case 'left': {
        return 'flex-start';
      }
      case 'right': {
        return 'flex-end';
      }
      case 'apart': {
        return 'space-between';
      }
    }
  }};

  ${BaseButton} {
    ${props =>
      props.$align === 'left' &&
      css`
        &:not(:first-of-type) {
          margin-left: ${spacing03};
        }
      `};

    ${props =>
      props.$align === 'right' &&
      css`
        &:not(:last-of-type) {
          margin-right: ${spacing03};
        }
      `};
  }
`;
